import { useEffect, useState } from "react";
import {
  createThread,
  deleteThread,
  listMessages,
  listThreads,
  renameThread,
  streamChatThread,
} from "./coach-api";
import { getCanvas, listVersions } from "./api";
import type { CoachMessage, CoachThread } from "./schema";
import { useToast } from "../ui/Toast";
import { useConfirm } from "../ui/ConfirmDialog";
import { ThreadSwitcher } from "./coach/ThreadSwitcher";
import { MessageList } from "./coach/MessageList";
import { MessageInput } from "./coach/MessageInput";

interface Props {
  sessionId: string;
  workflowName: string;
  currentVersion: number;
  onHome: () => void;
}

export function CoachPanel({
  sessionId,
  workflowName,
  currentVersion,
  onHome,
}: Props) {
  const toast = useToast();
  const confirm = useConfirm();

  // Session metadata — hydrated from getCanvas if not passed by caller.
  const [workflow, setWorkflow] = useState(workflowName);
  const [version, setVersion] = useState(currentVersion);

  const [threads, setThreads] = useState<CoachThread[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Hydrate session metadata if caller didn't supply it (OutputsScreen entry).
  useEffect(() => {
    if (workflow && version) return;
    void (async () => {
      try {
        const [c, v] = await Promise.all([
          getCanvas(sessionId),
          listVersions(sessionId).catch(() => ({ versions: [] })),
        ]);
        const latest = v.versions
          .map((x) => x.version)
          .reduce((a, b) => (b > a ? b : a), 0);
        setWorkflow((cur) => cur || c.workflowName);
        setVersion((cur) => cur || latest || 0);
      } catch (e) {
        toast.error((e as Error).message);
      }
    })();
  }, [sessionId, workflow, version, toast]);

  // Load thread list on mount.
  useEffect(() => {
    void (async () => {
      try {
        const list = await listThreads(sessionId);
        setThreads(list);
        if (list.length > 0) setActiveId(list[0].id);
      } catch (e) {
        toast.error((e as Error).message);
      } finally {
        setLoadingThreads(false);
      }
    })();
  }, [sessionId, toast]);

  // Load messages when active thread changes.
  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    setLoadingMessages(true);
    void (async () => {
      try {
        const list = await listMessages(sessionId, activeId);
        setMessages(list);
      } catch (e) {
        toast.error((e as Error).message);
      } finally {
        setLoadingMessages(false);
      }
    })();
  }, [sessionId, activeId, toast]);

  const activeThread = threads.find((t) => t.id === activeId) ?? null;

  const handleNewThread = async () => {
    try {
      const t = await createThread(sessionId);
      setThreads((all) => [t, ...all]);
      setActiveId(t.id);
      setMessages([]);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleRename = async (id: string, newTitle: string) => {
    try {
      const updated = await renameThread(sessionId, id, newTitle);
      setThreads((all) => all.map((t) => (t.id === id ? updated : t)));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDelete = async (id: string) => {
    const t = threads.find((x) => x.id === id);
    if (!t) return;
    const ok = await confirm({
      title: "Delete this dossier?",
      message: `"${t.title}" and all of its messages will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete",
      cancelLabel: "Keep",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteThread(sessionId, id);
      setThreads((all) => all.filter((x) => x.id !== id));
      if (activeId === id) {
        const next = threads.find((x) => x.id !== id);
        setActiveId(next?.id ?? null);
      }
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const actuallySend = async (threadId: string, content: string) => {
    const optimisticUserMsg: CoachMessage = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content,
      createdAt: Date.now(),
    };
    setMessages((m) => [...m, optimisticUserMsg]);
    setStreaming(true);
    setStreamingText("");
    let finalText = "";
    try {
      await streamChatThread(sessionId, threadId, content, {
        onToken: (delta) => {
          finalText += delta;
          setStreamingText((t) => t + delta);
        },
        onDone: ({ assistantMessageId, interrupted }) => {
          const assistantMsg: CoachMessage = {
            id: assistantMessageId ?? `tmp-asst-${Date.now()}`,
            role: "assistant",
            content: finalText + (interrupted && !finalText ? "" : ""),
            createdAt: Date.now(),
          };
          // Only commit an assistant entry if we actually streamed text.
          if (finalText.length > 0) {
            setMessages((m) => [...m, assistantMsg]);
          } else if (interrupted) {
            // The server emitted done:interrupted with no text — show a
            // small inline notice rather than a phantom empty bubble.
            toast.error("The coach didn't reply. Try again.");
          }
          setStreaming(false);
          setStreamingText("");
          // Refresh threads to pick up new title / updatedAt server-side.
          void listThreads(sessionId).then(setThreads).catch(() => {});
        },
        onError: (err) => {
          setStreaming(false);
          setStreamingText("");
          toast.error(err.message);
        },
      });
    } catch (e) {
      setStreaming(false);
      setStreamingText("");
      toast.error((e as Error).message);
    }
  };

  const handleSend = async (content: string) => {
    if (!activeId) {
      // No thread yet — auto-create one on first send.
      try {
        const t = await createThread(sessionId);
        setThreads((all) => [t, ...all]);
        setActiveId(t.id);
        await actuallySend(t.id, content);
      } catch (e) {
        toast.error((e as Error).message);
      }
      return;
    }
    await actuallySend(activeId, content);
  };

  const versionMismatch =
    activeThread && version > 0 && activeThread.snapshotVersion !== version;

  return (
    <div className="coach">
      <header className="coach__header">
        <div className="coach__header-left">
          <button className="btn btn--text" onClick={onHome}>
            ← Home
          </button>
          <span className="coach__eyebrow">Coach</span>
        </div>
        <div className="coach__header-center">
          <h1 className="coach__workflow">{workflow || "Canvas"}</h1>
          <div className="coach__header-meta">
            <ThreadSwitcher
              threads={threads}
              activeId={activeId}
              onSelect={setActiveId}
              onNew={handleNewThread}
              onRename={handleRename}
              onDelete={handleDelete}
            />
            {versionMismatch && (
              <span
                className="chip chip--warn coach__version-pill"
                title="The canvas has changed since this dossier started. The coach will use the latest version on the next turn."
              >
                <span className="chip__k">Snapshot</span>
                <span className="chip__v">
                  v{activeThread!.snapshotVersion} → v{version}
                </span>
              </span>
            )}
          </div>
        </div>
        <div className="coach__header-right" aria-hidden="true">
          <div className="coach__seal">§</div>
        </div>
      </header>

      <main className="coach__main">
        {loadingThreads ? (
          <div className="coach__loading">Opening the ledger…</div>
        ) : loadingMessages ? (
          <div className="coach__loading">Loading messages…</div>
        ) : (
          <MessageList
            messages={messages}
            streaming={streaming}
            streamingText={streamingText}
          />
        )}
      </main>

      <footer className="coach__footer">
        <MessageInput onSend={handleSend} disabled={streaming} />
      </footer>
    </div>
  );
}
